// src/features/wallet/WalletPage.tsx
import { useState } from 'react';
import { CheckCircle2, CircleOff, WalletCards } from 'lucide-react';
import { EmptyState, formatPersianNumber } from '../../app/components/foundation';
import { ControlPlaneDataSurface, ControlPlaneMetricStrip, ControlPlanePage, ControlPlaneSection } from '../../app/components/foundation/control-plane';
import { VaniaContentSkeleton, VaniaLoadingSystem } from '../../app/components/loading';
import { WalletTable } from './components';
import { useWallets } from './hooks/useWallets';
import './styles/wallet.css';

/**
 * Wallet Foundation. Same layout pattern as CommissionPage/
 * OrdersPage: a PageContainer shell, a feature-owned header, then
 * feature content below — RTL and Persian throughout, using only
 * existing tokens/primitives (Card, PageHeader) via
 * app/components/foundation.
 *
 * Every row here comes from features/wallet/data/wallet.mock.ts via
 * useWalletMock() — no wallet endpoint exists yet. useWalletMock is
 * the seam a later phase swaps for a real API hook (same shape,
 * `{ wallets }`); WalletTable only depends on the `wallets` prop, not
 * on where it came from, so that swap won't touch this file or
 * WalletTable.tsx.
 */
export function WalletPage() {
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const { wallets, meta, loading, error } = useWallets(page, search);
  const hasNextPage = page * meta.limit < meta.total;

  return (
    <ControlPlanePage
      className="vania-wallet-control-plane-page"
      title="مدیریت کیف پول‌ها"
      description="مشاهده و پیگیری کیف پول مشتریان در تمام تننت‌های پلتفرم."
      summary={wallets.length > 0 ? (
        <ControlPlaneMetricStrip
          items={[
            { label: 'کل کیف پول‌ها', value: formatPersianNumber(meta.total), icon: <WalletCards size={16} />, tone: 'primary' },
            { label: 'فعال', value: formatPersianNumber(wallets.filter((wallet) => wallet.status === 'active').length), icon: <CheckCircle2 size={16} />, tone: 'success' },
            { label: 'مسدود', value: formatPersianNumber(wallets.filter((wallet) => wallet.status === 'blocked').length), icon: <CircleOff size={16} />, tone: 'danger' },
            { label: 'موجودی صفحه', value: `${formatPersianNumber(wallets.reduce((total, wallet) => total + wallet.balance, 0))} تومان`, icon: <WalletCards size={16} />, tone: 'warning' },
          ]}
        />
      ) : undefined}
    >
      <ControlPlaneSection>
        <ControlPlaneDataSurface>
          {error && wallets.length === 0 ? <p className="vania-async-state vania-async-state-error" role="alert">{error}</p> : null}
          <div className="vania-wallet-toolbar">
            <label className="vania-wallet-search"><input type="search" value={search} onChange={(event) => { setSearch(event.target.value); setPage(1); }} placeholder="شناسه کاربر، Telegram ID یا موبایل" aria-label="جست‌وجوی کیف پول" /></label>
          </div>
          <div className="vania-wallet-table-region">
            {error && wallets.length === 0 ? null : loading && wallets.length === 0 ? <WalletTableSkeleton /> : !loading && wallets.length === 0 ? <EmptyState message="کیف پولی یافت نشد." /> : <WalletTable wallets={wallets} />}
          </div>
          {wallets.length > 0 ? <div className="vania-wallet-pagination"><button type="button" disabled={page <= 1} onClick={() => setPage((current) => current - 1)}>قبلی</button><span>صفحه {meta.page} از {Math.max(1, Math.ceil(meta.total / meta.limit))}</span><button type="button" disabled={!hasNextPage} onClick={() => setPage((current) => current + 1)}>بعدی</button></div> : null}
        </ControlPlaneDataSurface>
      </ControlPlaneSection>
    </ControlPlanePage>
  );
}

function WalletTableSkeleton() {
  return (
    <VaniaLoadingSystem mode="content" label="در حال بارگذاری کیف پول‌ها">
      <div className="vania-wallet-table-scroll" aria-hidden="true">
          <table className="vania-wallet-table">
            <thead>
              <tr>
                {['شناسه کاربر', 'کاربر', 'Telegram ID', 'موبایل', 'موجودی', 'وضعیت', 'تاریخ ایجاد', 'عملیات'].map((heading) => <th key={heading}>{heading}</th>)}
              </tr>
            </thead>
            <tbody>
              {Array.from({ length: 8 }, (_, rowIndex) => (
                <tr key={`wallet-skeleton-${rowIndex}`}>
                  {Array.from({ length: 8 }, (_, cellIndex) => (
                    <td key={`wallet-skeleton-${rowIndex}-${cellIndex}`}><VaniaContentSkeleton style={{ width: cellIndex === 0 ? '70%' : '55%' }} /></td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
      </div>
    </VaniaLoadingSystem>
  );
}
